import { format } from 'date-fns';
import { DateTime } from 'luxon';
import { getFlagValue } from 'flagging/hooks';
/**
 * Checks if a Date object is valid
 *
 * @param {Date} date - The date to be assessed
 * @returns {boolean} - Whether or not the date is valid
 */
export var isValidDate = function (date) {
    return date instanceof Date && !Number.isNaN(date.valueOf());
};
/**
 * Takes a date string and returns a fully formatted date string, e.g. Fri Oct 25, 7:27pm GMT-4
 * If the passed date string does not construct a valid date, then the original string is returned.
 *
 * @param {string} str - The string to be formatted
 * @returns {string} - The formatted string or the
 */
export var formatFullDateTime = function (str, includeYear, forceYear) {
    if (includeYear === void 0) { includeYear = false; }
    if (forceYear === void 0) { forceYear = false; }
    var date = new Date(str);
    var yearToken = includeYear ? getYearFormatToken(date, forceYear) : '';
    // Return the passed in string if it is not a valid date
    if (!isValidDate(date)) {
        return str;
    }
    var luxonDateTime = DateTime.local();
    // Shortened local timeZone of user, e.g. EST
    var timeZoneOffsetName = luxonDateTime.offsetNameShort;
    return "".concat(format(date, "E LLL d,".concat(yearToken ? " ".concat(yearToken, ",") : '', " h:mmaaa")), " ").concat(timeZoneOffsetName);
};
/**
 * This should be considered a workaround for an upstream bug. IE known issue is Diglet last sign in timestamp has no timezone data.
 * Takes a UTC date string and returns a fully formatted date string, e.g. Fri Oct 25, 7:27pm GMT-4
 * If the passed date string does not construct a valid date, then the original string is returned.
 *
 * @param {string} str - The string to be formatted
 * @returns {string} - The formatted string or the
 */
export var formatBrokenUpstreamUTCDateTime = function (str, includeYear, forceYear) {
    if (includeYear === void 0) { includeYear = false; }
    if (forceYear === void 0) { forceYear = false; }
    var date = new Date("".concat(str, " UTC"));
    // Return the passed in string if it is not a valid date
    if (!isValidDate(date)) {
        return str;
    }
    var yearToken = includeYear ? getYearFormatToken(date, forceYear) : '';
    var luxonDateTime = DateTime.local();
    // Shortened local timeZone of user, e.g. EST
    var timeZoneOffsetName = luxonDateTime.offsetNameShort;
    return "".concat(format(date, "E LLL d,".concat(yearToken ? " ".concat(yearToken, ",") : '', " h:mmaaa")), " ").concat(timeZoneOffsetName);
};
/**
 * Takes a date string or JS Date and returns a fully formatted date string, e.g. Fri Oct 25, 7:27pm GMT-4
 * If the passed date string does not construct a valid date, then the original string is returned.
 *
 * @param {string|Date} str - The string to be formatted
 * @returns {string} - The formatted string or the
 */
export var formatFullDate = function (str, includeYear, forceYear) {
    if (includeYear === void 0) { includeYear = false; }
    if (forceYear === void 0) { forceYear = false; }
    var date = typeof str === 'string' ? new Date(str) : str;
    // Return the passed in string if it is not a valid date
    if (!isValidDate(date)) {
        return typeof str === 'string' ? str : '';
    }
    var yearToken = includeYear ? getYearFormatToken(date, forceYear) : '';
    return "".concat(format(date, "E LLL d".concat(yearToken ? ", ".concat(yearToken) : '')));
};
/**
 * Format a date-only string such that result does not assume timezone and always shows the original date
 */
export var formatTimezoneAgnosticFullDate = function (str, includeYear, forceYear) {
    if (includeYear === void 0) { includeYear = false; }
    if (forceYear === void 0) { forceYear = false; }
    var date = new Date(str);
    var dateOnly = new Date(date.valueOf() + date.getTimezoneOffset() * 60 * 1000);
    var yearToken = includeYear ? getYearFormatToken(date, forceYear) : '';
    return "".concat(format(dateOnly, "E LLL d".concat(yearToken ? ", ".concat(yearToken) : '')));
};
/**
 * Takes a date string and returns a formatted calendar date string, e.g. Oct 25, 2022
 * If the passed date string does not construct a valid date, then the original string is returned.
 *
 * @param {string} str - The string to be formatted
 * @returns {string} - The formatted string or the
 */
export var formatCalendarDate = function (str) {
    var date = new Date(str);
    // Return the passed in string if it is not a valid date
    if (!isValidDate(date)) {
        return typeof str === 'string' ? str : str.toString();
    }
    return "".concat(format(date, 'LLL d, yyyy'));
};
export var getAgeFromFormattedDob = function (dob) {
    if (!dob)
        return '';
    return getAgeFromDateTime(DateTime.fromFormat(dob, 'MM/dd/yyyy'));
};
export var getAgeFromDateTime = function (dob) {
    var age = Math.floor(Math.abs(dob.diffNow('years').years));
    if (Number.isNaN(age) || age < 0)
        return '';
    return age;
};
var getYearFormatToken = function (date, forceYear) {
    var flagValue = getFlagValue('SHOW_DATE_YEAR');
    var today = new Date();
    if (flagValue === 'none') {
        return '';
    }
    else if (flagValue === 'always' || forceYear) {
        return 'yyyy';
    }
    else if (flagValue === 'different-year' && date.getFullYear() !== today.getFullYear()) {
        return 'yyyy';
    }
};
export var getPreviousDayDate = function (date) {
    var previousDay = new Date(date);
    previousDay.setDate(previousDay.getDate() - 1);
    return previousDay;
};
